import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  message,
  Modal,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import React, { useState } from 'react';
import {
  CampaignOverrides,
  CampaignPreviewResponse,
  ComplianceCheckRequest,
  ComplianceResult,
  TwilioOnboardingConfig,
  TwilioOnboardingStatus,
} from '../../types/twilio-onboarding';
import {
  fetchCampaignPreview,
  fetchOnboardingStatus,
  resubmitCampaign,
  runComplianceCheck,
  updateOnboardingConfig,
} from '../../utils/twilio-onboarding-api';
import { AuthorizedRepForm } from './AuthorizedRepForm';
import { BusinessInfoForm } from './BusinessInfoForm';
import { CampaignInfoForm } from './CampaignInfoForm';
import { ComplianceCheckPanel } from './ComplianceCheckPanel';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

interface RejectionRecoveryPanelProps {
  apiUrl: string;
  accessToken: string;
  clientId: string;
  status: TwilioOnboardingStatus;
  onStatusUpdate: (status: TwilioOnboardingStatus) => void;
}

export const RejectionRecoveryPanel: React.FC<RejectionRecoveryPanelProps> = ({
  apiUrl,
  accessToken,
  clientId,
  status,
  onStatusUpdate,
}) => {
  const [editForm] = Form.useForm();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [configAvailable, setConfigAvailable] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resubmitting, setResubmitting] = useState(false);
  const [complianceLoading, setComplianceLoading] = useState(false);
  const [complianceResult, setComplianceResult] =
    useState<ComplianceResult | null>(null);

  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [campaignModalLoading, setCampaignModalLoading] = useState(false);
  const [campaignPreview, setCampaignPreview] =
    useState<CampaignPreviewResponse | null>(null);
  const [campaignError, setCampaignError] = useState<string | null>(null);
  const [editDescription, setEditDescription] = useState('');
  const [editMessageFlow, setEditMessageFlow] = useState('');
  const [editSamples, setEditSamples] = useState<string[]>([]);
  const [campaignOverrides, setCampaignOverrides] = useState<CampaignOverrides>(
    {},
  );

  const isCampaignRejection = status.rejection_step === 'campaign';

  const handleEditConfig = async () => {
    setEditModalOpen(true);
    setEditLoading(true);
    editForm.resetFields();
    try {
      const fresh = await fetchOnboardingStatus(apiUrl, accessToken, clientId);
      if (fresh?.onboarding_config) {
        editForm.setFieldsValue({
          ...fresh.onboarding_config,
          area_code: fresh.onboarding_config.area_code,
        });
        setConfigAvailable(true);
      } else if (status.onboarding_config) {
        editForm.setFieldsValue({
          ...status.onboarding_config,
          area_code: status.onboarding_config.area_code,
        });
        setConfigAvailable(true);
      } else {
        setConfigAvailable(false);
      }
    } catch {
      if (status.onboarding_config) {
        editForm.setFieldsValue({
          ...status.onboarding_config,
          area_code: status.onboarding_config.area_code,
        });
        setConfigAvailable(true);
      } else {
        setConfigAvailable(false);
      }
    } finally {
      setEditLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    let values;
    try {
      values = await editForm.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    try {
      const config: Partial<TwilioOnboardingConfig> = {
        campaign_info: values.campaign_info,
        business_info: values.business_info,
        authorized_rep: values.authorized_rep,
        area_code: values.area_code,
      };
      await updateOnboardingConfig(apiUrl, accessToken, clientId, config);
      message.success('Configuration updated');
      setEditModalOpen(false);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Failed to update configuration';
      if (msg.includes('404') || msg.includes('501')) {
        message.info(
          'Config update endpoint is not yet available on the backend.',
        );
      } else {
        message.error(msg);
      }
    } finally {
      setSaving(false);
    }
    try {
      const fresh = await fetchOnboardingStatus(apiUrl, accessToken, clientId);
      if (fresh) {
        onStatusUpdate(fresh);
      }
    } catch {
      // Status refresh is best-effort; save already succeeded
    }
  };

  const handleEditCampaignContent = async () => {
    setCampaignModalOpen(true);
    setCampaignModalLoading(true);
    setCampaignError(null);
    try {
      const data = await fetchCampaignPreview(apiUrl, accessToken, clientId);
      setCampaignPreview(data);
      setEditDescription(data.description);
      setEditMessageFlow(data.message_flow);
      setEditSamples([...data.message_samples]);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Failed to load campaign content';
      if (msg.includes('404') || msg.includes('501')) {
        setCampaignError(
          'Campaign preview endpoint is not yet available on the backend.',
        );
      } else {
        setCampaignError(msg);
      }
    } finally {
      setCampaignModalLoading(false);
    }
  };

  const handleSaveCampaignContent = () => {
    const overrides: CampaignOverrides = {};
    if (campaignPreview && editDescription !== campaignPreview.description) {
      overrides.description_override = editDescription;
    }
    if (campaignPreview && editMessageFlow !== campaignPreview.message_flow) {
      overrides.message_flow_override = editMessageFlow;
    }
    if (
      campaignPreview &&
      JSON.stringify(editSamples) !==
        JSON.stringify(campaignPreview.message_samples)
    ) {
      overrides.message_samples_override = editSamples;
    }
    setCampaignOverrides(overrides);
    setCampaignModalOpen(false);
    if (Object.keys(overrides).length > 0) {
      message.success(
        'Campaign content changes saved. Click "Resubmit Campaign" to apply.',
      );
    } else {
      message.info('No changes made to campaign content.');
    }
  };

  const handleComplianceCheck = async () => {
    const formValues = editForm.getFieldsValue();
    const config = status.onboarding_config;
    const campaignInfo = formValues?.campaign_info ?? config?.campaign_info;
    if (!campaignInfo?.form_url) {
      message.error(
        'No form URL available. Please edit the configuration first.',
      );
      return;
    }
    setComplianceLoading(true);
    try {
      const params: ComplianceCheckRequest = {
        form_url: campaignInfo.form_url,
        client_id: parseInt(clientId, 10),
        trigger_text: campaignInfo.form_button_text,
        privacy_policy_url: campaignInfo.privacy_policy_url,
        terms_url: campaignInfo.terms_url,
      };
      const result = await runComplianceCheck(apiUrl, accessToken, params);
      setComplianceResult(result);
    } catch (err) {
      message.error(
        err instanceof Error ? err.message : 'Compliance check failed',
      );
    } finally {
      setComplianceLoading(false);
    }
  };

  const handleResubmit = async () => {
    setResubmitting(true);
    try {
      const overrides =
        Object.keys(campaignOverrides).length > 0
          ? campaignOverrides
          : undefined;
      const result = await resubmitCampaign(
        apiUrl,
        accessToken,
        clientId,
        overrides,
      );
      message.success('Campaign resubmitted successfully');
      onStatusUpdate(result);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Failed to resubmit campaign';
      if (msg.includes('404') || msg.includes('501')) {
        message.info(
          'Campaign resubmit endpoint is not yet available on the backend.',
        );
      } else {
        message.error(msg);
      }
    } finally {
      setResubmitting(false);
    }
  };

  const hasOverrides = Object.keys(campaignOverrides).length > 0;

  return (
    <>
      <Alert
        description={
          <div>
            <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 8 }}>
              {status.rejection_details}
            </Paragraph>
            {!isCampaignRejection && (
              <Text type="secondary">
                Profile and brand rejections may require contacting Twilio
                support to resolve.
              </Text>
            )}
          </div>
        }
        message={`Rejected${status.rejection_step ? ` at ${status.rejection_step} step` : ''}`}
        showIcon
        style={{ marginBottom: 24 }}
        type="error"
      />

      <Space style={{ marginBottom: 24 }} wrap>
        <Button onClick={handleEditConfig}>Edit Configuration</Button>
        {isCampaignRejection && (
          <Button onClick={handleEditCampaignContent}>
            Edit Campaign Content
          </Button>
        )}
        <Button loading={complianceLoading} onClick={handleComplianceCheck}>
          Re-run Compliance Check
        </Button>
        {isCampaignRejection && (
          <Button
            loading={resubmitting}
            onClick={handleResubmit}
            style={{ backgroundColor: '#5b1eaf', borderColor: '#5b1eaf' }}
            type="primary"
          >
            Resubmit Campaign{hasOverrides ? ' (with edits)' : ''}
          </Button>
        )}
      </Space>

      {(complianceResult || complianceLoading) && (
        <ComplianceCheckPanel
          loading={complianceLoading}
          onRunCheck={handleComplianceCheck}
          result={complianceResult}
        />
      )}

      {/* Edit Configuration Modal */}
      <Modal
        confirmLoading={saving}
        okText="Save Changes"
        onCancel={() => setEditModalOpen(false)}
        onOk={handleSaveConfig}
        open={editModalOpen}
        title="Edit Onboarding Configuration"
        width={800}
      >
        {editLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : (
          <>
            {!configAvailable && (
              <Alert
                description="The original onboarding configuration could not be loaded. Please fill in the fields below with the corrected values."
                message="Previous configuration not available"
                showIcon
                style={{ marginBottom: 16 }}
                type="info"
              />
            )}
            <Form form={editForm} layout="vertical">
              <Card
                size="small"
                style={{ marginBottom: 16 }}
                title="Business Information"
              >
                <BusinessInfoForm form={editForm} />
              </Card>
              <Card
                size="small"
                style={{ marginBottom: 16 }}
                title="Authorized Representative"
              >
                <AuthorizedRepForm form={editForm} />
              </Card>
              <Card size="small" title="Campaign Configuration">
                <CampaignInfoForm form={editForm} />
              </Card>
            </Form>
          </>
        )}
      </Modal>

      {/* Edit Campaign Content Modal */}
      <Modal
        okText="Save Changes"
        onCancel={() => setCampaignModalOpen(false)}
        onOk={handleSaveCampaignContent}
        open={campaignModalOpen}
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
        title="Edit Campaign Content"
        width={900}
      >
        {campaignModalLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : campaignError ? (
          <Alert message={campaignError} showIcon type="info" />
        ) : campaignPreview ? (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Description
              </Text>
              <TextArea
                onChange={e => setEditDescription(e.target.value)}
                rows={8}
                value={editDescription}
              />
            </div>

            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Message Flow (Opt-In Description)
              </Text>
              <TextArea
                onChange={e => setEditMessageFlow(e.target.value)}
                rows={6}
                value={editMessageFlow}
              />
            </div>

            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Message Samples
              </Text>
              {editSamples.map((sample, i) => (
                <div key={i} style={{ marginBottom: 8 }}>
                  <Text
                    style={{ display: 'block', marginBottom: 4 }}
                    type="secondary"
                  >
                    Sample #{i + 1}
                  </Text>
                  <TextArea
                    onChange={e => {
                      const updated = [...editSamples];
                      updated[i] = e.target.value;
                      setEditSamples(updated);
                    }}
                    rows={2}
                    value={sample}
                  />
                </div>
              ))}
            </div>

            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Keywords
              </Text>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text type="secondary">Opt-in: </Text>
                  {campaignPreview.opt_in_keywords.map(k => (
                    <Tag color="green" key={k}>
                      {k}
                    </Tag>
                  ))}
                </div>
                <div>
                  <Text type="secondary">Opt-out: </Text>
                  {campaignPreview.opt_out_keywords.map(k => (
                    <Tag color="red" key={k}>
                      {k}
                    </Tag>
                  ))}
                </div>
                <div>
                  <Text type="secondary">Help: </Text>
                  {campaignPreview.help_keywords.map(k => (
                    <Tag color="blue" key={k}>
                      {k}
                    </Tag>
                  ))}
                </div>
              </Space>
            </div>

            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Standard Messages
              </Text>
              <Card
                size="small"
                style={{ marginBottom: 8, background: '#f6ffed' }}
                title="Opt-In Response"
              >
                <Text>{campaignPreview.opt_in_message}</Text>
              </Card>
              <Card
                size="small"
                style={{ marginBottom: 8, background: '#fff2f0' }}
                title="Opt-Out Response"
              >
                <Text>{campaignPreview.opt_out_message}</Text>
              </Card>
              <Card
                size="small"
                style={{ background: '#e6f7ff' }}
                title="Help Response"
              >
                <Text>{campaignPreview.help_message}</Text>
              </Card>
            </div>
          </Space>
        ) : null}
      </Modal>
    </>
  );
};
